<!--
  PasswordField.svelte — Password input with visibility toggle.

  Extends FormField semantics but adds a clickable eye icon to
  show/hide the password.  Used on Login, Register, ResetPassword,
  and ChangePassword forms.

  @prop {string} id        — HTML id for the input.
  @prop {string} label     — Label text.
  @prop {string} value     — Bound password value.
  @prop {string} [placeholder] — Placeholder text.
  @prop {string} [error]   — Error message.
  @prop {string} [helper]  — Helper text.
  @prop {boolean} [disabled] — Disabled state.
  @prop {boolean} [required] — Required state.
-->
<script lang="ts">
	import { Input, Label, Helper } from 'flowbite-svelte';
	import { t } from '../i18n';

	
	
	
	
	
	
	
	
	interface Props {
		/** HTML id for the input element. */
		id: string;
		/** Visible label text. */
		label: string;
		/** Bound password value. */
		value?: string;
		/** Placeholder text. */
		placeholder?: string;
		/** Error message (red) displayed below the input. */
		error?: string;
		/** Helper text (gray) shown when there is no error. */
		helper?: string;
		/** Whether the input is disabled. */
		disabled?: boolean;
		/** Whether the field is required. */
		required?: boolean;
		/** Callback fired on the input event. */
		oninput?: (_e: Event) => void;
		/** Callback fired on the keypress event. */
		onkeypress?: (_e: KeyboardEvent) => void;
		/** Callback fired on the blur event. */
		onblur?: (_e: FocusEvent) => void;
	}

	let {
		id,
		label,
		value = $bindable(''),
		placeholder = '',
		error = '',
		helper = '',
		disabled = false,
		required = false,
		oninput,
		onkeypress,
		onblur
	}: Props = $props();

	/** Controls whether the password is shown as plain text. */
	let showPassword = $state(false);

	/** Toggle password visibility. */
	function toggleVisibility(): void {
		showPassword = !showPassword;
	}
</script>

<div class="mb-4">
	<Label for={id} class="mb-2 {error ? 'text-red-600 dark:text-red-500' : ''}">{label}</Label>
	<div class="relative">
		<Input
			{id}
			type={showPassword ? 'text' : 'password'}
			{placeholder}
			{disabled}
			{required}
			bind:value
			color={error ? 'red' : 'default'}
			{oninput}
			{onkeypress}
			{onblur}
		/>
		<button
			type="button"
			class="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
			onclick={toggleVisibility}
			tabindex="-1"
			aria-label={showPassword ? $t('Hide password') : $t('Show password')}
		>
			<i class="fa-solid {showPassword ? 'fa-eye-slash' : 'fa-eye'} h-5 w-5"></i>
		</button>
	</div>
	{#if error}
		<Helper class="mt-1" color="red">{error}</Helper>
	{:else if helper}
		<Helper class="mt-1" color="gray">{helper}</Helper>
	{/if}
</div>
